import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import {
  useArmorTiers,
  useEquipment,
  useUpgradeArmor,
  useUpgradeWeapon,
  useWeaponTiers,
} from '@/api/queries';
import { useAuthStore } from '@/store/authStore';
import { useModalPayload, useUIStore } from '@/store/uiStore';
import { formatNumber } from '@/lib/format';
import { hapticNotify } from '@/lib/telegram';
import { cn } from '@/lib/cn';
import type { ArmorTier, ModalId, WeaponTier } from '@/types';

interface UpgradeModalProps {
  kind: 'weapon' | 'armor';
}

interface Payload {
  waifuId: number;
}

interface BonusBreakdown {
  hp: number;
  str: number;
  dex: number;
  int: number;
}

function readBonuses(slot: {
  hp_bonus?: number;
  str_bonus?: number;
  dex_bonus?: number;
  int_bonus?: number;
} | null | undefined): BonusBreakdown {
  return {
    hp: slot?.hp_bonus ?? 0,
    str: slot?.str_bonus ?? 0,
    dex: slot?.dex_bonus ?? 0,
    int: slot?.int_bonus ?? 0,
  };
}

export function UpgradeModal({ kind }: UpgradeModalProps) {
  const modalId: ModalId = kind === 'weapon' ? 'upgrade-weapon' : 'upgrade-armor';
  const open = useUIStore((s) => s.isModalOpen(modalId));
  const closeModal = useUIStore((s) => s.closeModal);
  const payload = useModalPayload<Payload>(modalId);
  const userId = useAuthStore((s) => s.userId) ?? 0;
  const pushNotification = useUIStore((s) => s.pushNotification);

  const waifuId = payload?.waifuId ?? 0;
  const { data } = useEquipment(waifuId, userId);
  const { data: weaponTiersData } = useWeaponTiers();
  const { data: armorTiersData } = useArmorTiers();
  const upgradeWeapon = useUpgradeWeapon(waifuId, userId);
  const upgradeArmor = useUpgradeArmor(waifuId, userId);

  if (!open || !waifuId) return null;

  const slot = kind === 'weapon' ? data?.equipment.weapon : data?.equipment.armor;
  const cost =
    kind === 'weapon'
      ? data?.upgrade_costs?.weapon_upgrade_cost ?? 0
      : data?.upgrade_costs?.armor_upgrade_cost ?? 0;
  const mutation = kind === 'weapon' ? upgradeWeapon : upgradeArmor;

  const tier = slot?.tier ?? slot?.level ?? 1;
  const typeKey =
    kind === 'weapon' ? slot?.weapon_type : slot?.armor_type;

  let nextTier: WeaponTier | ArmorTier | undefined;
  if (kind === 'weapon' && typeKey) {
    nextTier = weaponTiersData?.tiers.find(
      (t) => t.weapon_type === typeKey && t.tier === tier + 1,
    );
  } else if (kind === 'armor' && typeKey) {
    nextTier = armorTiersData?.tiers.find(
      (t) => t.armor_type === typeKey && t.tier === tier + 1,
    );
  }

  const currentBonuses = readBonuses(slot);
  const nextBonuses = readBonuses(nextTier);

  const itemName = slot?.name ?? (kind === 'weapon' ? 'Оружие' : 'Доспех');
  const title =
    kind === 'weapon' ? 'Улучшить оружие' : 'Улучшить доспех';

  return (
    <Modal open={open} onClose={() => closeModal(modalId)} title={title}>
      <div className="p-4 space-y-3">
        <div className="text-center">
          <div className="text-base text-white font-medium">{itemName}</div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <TierBlock
            title={`Текущий T${tier}`}
            bonuses={currentBonuses}
          />
          {nextTier ? (
            <TierBlock
              title={`Следующий T${tier + 1}`}
              bonuses={nextBonuses}
              prev={currentBonuses}
              highlight
            />
          ) : (
            <div className="rounded-xl bg-bg-elevated border border-white/5 p-3 flex items-center justify-center text-white/40 text-sm text-center">
              Максимальный тир
            </div>
          )}
        </div>

        {cost > 0 ? (
          <div className="text-sm text-white/80 text-center">
            Стоимость:{' '}
            <span className="text-stardust font-medium">
              ⭐ {formatNumber(cost)}
            </span>
          </div>
        ) : (
          <div className="text-sm text-white/50 text-center">
            Максимальный уровень
          </div>
        )}

        <Button
          fullWidth
          disabled={!cost || mutation.isPending || !nextTier}
          onClick={async () => {
            try {
              await mutation.mutateAsync();
              hapticNotify('success');
              pushNotification({ type: 'success', message: 'Улучшено!' });
              closeModal(modalId);
            } catch (err) {
              hapticNotify('error');
              const message =
                err instanceof Error ? err.message : 'Не удалось улучшить';
              pushNotification({ type: 'error', message });
            }
          }}
        >
          {mutation.isPending ? 'Улучшаем…' : 'Улучшить'}
        </Button>
      </div>
    </Modal>
  );
}

function TierBlock({
  title,
  bonuses,
  prev,
  highlight,
}: {
  title: string;
  bonuses: BonusBreakdown;
  prev?: BonusBreakdown;
  highlight?: boolean;
}) {
  const rows: Array<{ key: keyof BonusBreakdown; label: string }> = [
    { key: 'hp', label: 'HP' },
    { key: 'str', label: 'STR' },
    { key: 'dex', label: 'DEX' },
    { key: 'int', label: 'INT' },
  ].filter((r) => bonuses[r.key as keyof BonusBreakdown] > 0 || (prev && prev[r.key as keyof BonusBreakdown] > 0)) as Array<{
    key: keyof BonusBreakdown;
    label: string;
  }>;

  return (
    <div
      className={cn(
        'rounded-xl p-3 space-y-1.5 border',
        highlight
          ? 'bg-accent/10 border-accent/40'
          : 'bg-bg-elevated border-white/5',
      )}
    >
      <div
        className={cn(
          'text-[11px] uppercase tracking-wider font-semibold',
          highlight ? 'text-accent' : 'text-white/55',
        )}
      >
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-white/40">—</div>
      ) : (
        <div className="space-y-0.5">
          {rows.map((row) => {
            const value = bonuses[row.key];
            const prevValue = prev?.[row.key];
            const delta =
              prev !== undefined && value !== prevValue
                ? value - (prevValue ?? 0)
                : 0;
            return (
              <div
                key={row.key}
                className="flex items-center justify-between text-[12px] tabular-nums"
              >
                <span className="text-white/55">{row.label}</span>
                <span className="text-white font-medium">
                  +{value}
                  {delta > 0 && (
                    <span className="text-success ml-1 text-[10px]">
                      (+{delta})
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
